import React, { useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import PaymentQr from './PaymentQr';
import { formatDateTime, getSessionById, toNumber } from '../services/marketplace';
import { useI18n } from '../i18n/I18nContext';
import { translateSessionName } from '../i18n/sessionCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import '../style/Payment.css';

const formatPaymentAmount = (amount, currency = 'EUR') => {
  const value = toNumber(amount).toFixed(2);
  return String(currency).toUpperCase() === 'CZK' ? `${value} Kč` : `€${value}`;
};

const SessionPayment = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { t, language } = useI18n();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !id) return undefined;
    let initial = true;
    getSessionById(id).then((row) => {
      if (!row || row.userId !== user.uid) throw new Error(t('workflow.payment.notFound'));
      setSession(row);
      setLoading(false);
      initial = false;
    }).catch((nextError) => {
      setError(getLocalizedError(nextError, language, t('workflow.payment.loadFailed')));
      setLoading(false);
      initial = false;
    });
    const unsubscribe = onSnapshot(doc(db, 'sessions', id), (snap) => {
      if (snap.exists()) {
        setSession({ id: snap.id, source: 'sessions', ...snap.data() });
        setLoading(false);
      } else if (!initial) {
        setError(t('workflow.payment.notFound'));
      }
    }, (nextError) => {
      setError(getLocalizedError(nextError, language, t('workflow.payment.statusFailed')));
      setLoading(false);
    });
    return unsubscribe;
  }, [id, language, t, user]);

  const state = useMemo(() => {
    if (['paid', 'confirmed', 'completed'].includes(session?.status)) return 'paid';
    if (['expired', 'cancelled'].includes(session?.status)) return 'closed';
    return 'pending';
  }, [session?.status]);

  const sessionName = translateSessionName(session?.sessionName, t, t('workflow.payment.reading'));

  if (authLoading || loading) return <><Header /><main className="payment-page"><div className="payment-state-card">{t('workflow.payment.loading')}</div></main><Footer /></>;
  if (!user) return <><Header /><main className="payment-page"><div className="payment-state-card"><h1>{t('workflow.payment.loginTitle')}</h1><Link className="primary-button" to="/login">{t('nav.login')}</Link></div></main><Footer /></>;
  if (error || !session) return <><Header /><main className="payment-page"><div className="payment-state-card error"><h1>{t('workflow.payment.unavailable')}</h1><p>{error}</p><Link to="/user-dashboard">{t('workflow.payment.backDashboard')}</Link></div></main><Footer /></>;

  return (
    <div>
      <Header />
      <main className="payment-page">
        <section className="payment-heading">
          <p className="eyebrow">{t('workflow.payment.eyebrow')}</p>
          <h1>{state === 'paid' ? t('workflow.payment.paidTitle') : state === 'closed' ? t('workflow.payment.closedTitle') : t('workflow.payment.pendingTitle')}</h1>
          <p>{state === 'pending'
            ? t('workflow.payment.pendingBody')
            : state === 'paid'
              ? t('workflow.payment.paidBody')
              : t('workflow.payment.closedBody')}</p>
        </section>

        <section className="payment-layout">
          {state === 'pending' && (
            <article className="payment-card qr-card">
              <PaymentQr
                amount={toNumber(session.price)}
                currency={session.currency || 'EUR'}
                variableSymbol={session.variableSymbol}
                note={`Fate Room ${sessionName}`}
                iban={session.tellerIban}
                beneficiaryName={session.tellerName}
              />
            </article>
          )}
          <article className="payment-card payment-details">
            <h2>{t('workflow.payment.details')}</h2>
            <dl>
              <div><dt>{t('workflow.payment.teller')}</dt><dd>{session.tellerName || '—'}</dd></div>
              <div><dt>{t('workflow.payment.session')}</dt><dd>{sessionName}</dd></div>
              <div><dt>{t('workflow.payment.date')}</dt><dd>{formatDateTime(session)}</dd></div>
              <div><dt>{t('workflow.payment.amount')}</dt><dd>{formatPaymentAmount(session.price, session.currency)}</dd></div>
              <div><dt>IBAN</dt><dd>{session.tellerIban || '—'}</dd></div>
              <div><dt>{t('workflow.payment.variable')}</dt><dd>{session.variableSymbol || '—'}</dd></div>
            </dl>
            {state === 'pending' && <div className="payment-waiting"><strong>{t('workflow.payment.waiting')}</strong><span>{t('workflow.payment.held')}</span></div>}
            <Link className="primary-button" to="/user-dashboard">{state === 'paid' ? t('workflow.payment.openDashboard') : t('workflow.payment.backSessions')}</Link>
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SessionPayment;
